import Loader from "@/components/common/Loader";
import ModalWithAction from "@/components/common/ModalWithAction";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useCloseMapperNote, useI18N } from "common/hooks";
import { MapperNotes } from "common/types";

import { useEffect, useState } from "react";

type MapperNoteView = {
  note: MapperNotes;
  onRefresh?: () => void;
};

function MapperNoteView({ note, onRefresh }: MapperNoteView) {
  const i18n = useI18N();
  const [opened, setOpened] = useState(true);
  const isDark = useDarkMode();
  const { closeNote, error, isLoading, data } = useCloseMapperNote();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const showToast = useCustomToast();
  useEffect(() => {
    if (error) {
      console.log(`closeNote error: ${error}`);
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      console.log(`closeNote data: ${data.message}`);
      setIsModalOpen(false);
      showToast(data.message, "success");
      setTimeout(() => {
        onRefresh();
      }, 1000);
    }
  }, [data]);

  const formatedDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatedDateTime = (date: string) => {
    const d = new Date(date);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // Ensures AM/PM format
    });
  };

  const formatedDateShort = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatedDateTimeShort = (date: string) => {
    const d = new Date(date);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // Ensures AM/PM format
    });
  };

  return (
    <div className="mb-2 border border-primary-200 rounded-xl p-4 h-full flex flex-col">
      <div className="self-stretch min-w-11 min-h-11 py-2 inline-flex justify-center items-start gap-4 flex-wrap content-start">
        <div className="flex-1 min-h-6 inline-flex flex-col justify-center items-start">
          <div className="self-stretch justify-center ucfirst text-primary-800 dark:text-primary-300 paragraph-medium-semi-bold">
            {note.action_title}
          </div>
          {note.business_notes_action_id === 15 && note.action_on_date && !note.return_on_date ? (
            <div className="self-stretch justify-center text-neutral-700 paragraph-xsmall-medium">
              {" "}
              make venue not visible to mappers until {formatedDateTimeShort(note.action_on_date)}
            </div>
          ) : note.business_notes_action_id === 8 && note.return_on_date ? (
            <div className="self-stretch justify-center text-neutral-700 paragraph-xsmall-medium">
              {" "}
              on {formatedDateTimeShort(note.return_on_date)}
            </div>
          ) : null}
        </div>
        <div className="flex justify-start items-center">
          <div className="flex flex-row gap-2">
            <button
              disabled={status === "pending"}
              onClick={() => {
                setIsModalOpen(true);
              }}
              className="w-10 h-10 relative bg-primary-50 dark:bg-[#313A71] border border-primary-200 rounded-lg flex items-center justify-center hover:bg-primary-100 dark:hover:bg-[#3F4A8A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-5 h-5">
                <img
                  src={
                    isDark
                      ? "/images/CheckCircleWhiteBg_Dark.svg"
                      : "/images/CheckCircleWhiteBg.svg"
                  }
                  alt="Resolve note"
                  className="w-full h-full"
                />
              </div>
            </button>
          </div>
        </div>
      </div>

      {note.location_name && note.location_name !== "N/A" && (
        <>
          <div className="self-stretch flex flex-col justify-start items-start gap-2.5 border border-primary-150"></div>
          <div className="self-stretch py-2 inline-flex justify-start items-center gap-4">
            <div className="flex-1 min-h-6 inline-flex flex-col justify-center items-start">
              <div className="self-stretch justify-center">
                <span className="text-primary-800 dark:text-primary-300 paragraph-medium-semi-bold">
                  Location :
                </span>
                <span className="text-primary-800 dark:text-primary-300 paragraph-small-medium">
                  {" "}
                  {note.location_name}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="self-stretch flex flex-col justify-start items-start gap-2.5 border border-primary-150"></div>

      <div className="self-stretch min-w-11 min-h-11 py-2 flex flex-col justify-center items-center gap-1">
        <div className="self-stretch justify-center text-neutral-700 dark:text-primary-300 paragraph-xsmall-medium">
          {formatedDateShort(note.created_at)} • {note.userName}
        </div>
        <div className="self-stretch min-h-6 flex flex-col justify-center items-start paragraph-small-medium text-primary-800 dark:text-primary-300">
          <div className="self-stretch justify-center ucfirst paragraph-small-medium text-primary-800 dark:text-primary-300">
            {note.notes}
          </div>
        </div>
      </div>

      {isLoading && <Loader />}

      <ModalWithAction
        isModalOpen={isModalOpen}
        title={i18n.get("resolveNote")}
        subtitle={i18n.get("areYouSureYouWantToResolveThisNote")}
        cancelButtonTitle={i18n.get("cancel")}
        actionButtonTitle={i18n.get("resolveNoteButton")}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onClick={() => {
          closeNote({ issueId: (note as any).issue_id.toString() });
        }}
        type="warning"
        actionButtonType="warning"
      />
    </div>
  );
}

export default MapperNoteView;
